import { DateTime } from 'luxon'
import {
  LeisureWebApiProductTypeCode,
  TLeisureWebApiProductTypeCode,
} from '~/api/services/leisure-web-api/index.type'
import {
  BusMashup,
  LeisureMashup,
  PropertyMashup,
  TrainMashup,
} from '~/api/routes/domestic/order/controller/type'
import { formatISODate } from '~/api/utils/datetime'

const domesticProductTypeToUseType = (type: 'R' | 'S' | 'D') => {
  const productTypeObject = {
    R: 'DAY_USE',
    S: 'OVER_NIGHT',
    D: 'MULTI_NIGHTS',
  }
  return productTypeObject[type]
}

export const getLeisureProductCategoryForRequest = (
  code: keyof typeof LeisureWebApiProductTypeCode
): TLeisureWebApiProductTypeCode => {
  return LeisureWebApiProductTypeCode[code]
}

export const convertPropertyMashupToKinesisData = (properties: PropertyMashup) => {
  return properties.map(property => {
    return {
      propertyId: property.propertyId,
      roomTypeId: property.roomTypeId ?? 0,
      useType: domesticProductTypeToUseType(property.useType),
      price: String(property.price ?? 0),
      checkInDate: formatISODate(property.checkInDate, 'yyyy-MM-dd'),
      checkOutDate: DateTime.fromISO(property.checkOutDate).toFormat('yyyy-MM-dd'),
      checkInTime: DateTime.fromISO(property.checkInAt).toFormat('yyyy-MM-dd HH:mm:ss'),
      checkOutTime: DateTime.fromISO(property.checkOutAt).toFormat('yyyy-MM-dd HH:mm:ss'),
      badgeMemberCode: property.ratePlan.memberClass ?? '',
    }
  })
}

export const convertLeisureMashupToKinesisData = (leisures: LeisureMashup) => {
  return leisures.map(leisure => {
    return {
      items: leisure.leisureProductVariants.map(variant => ({
        dealId: String(leisure.id),
        optionId: String(variant.id),
        optionName: variant.variantName,
        price: String(variant.price ?? 0),
        quantity: variant.quantity,
        purchaseOptionTypeCode: leisure.purchaseOptionTypeCode,
        productOptionGroupName: variant.productOptionGroup.productOptionGroupName ?? '',
        productOptionGroupId: `${variant.productOptionGroup.productOptionGroupId}`,
      })),
      productType: getLeisureProductCategoryForRequest(leisure.productTypeCode),
    }
  })
}

export const convertTrainMashupToKinesisData = (trains: TrainMashup) => {
  return trains.map(train => {
    const tickets = train.returns ? [train.single, train.returns] : [train.single]
    return {
      price: train.price ?? 0,
      trainMeta: tickets.map(ticket => {
        return {
          departureStationCode: ticket.departureStationCode,
          departureStation: ticket.departureStationName,
          arrivalStation: ticket.arrivalStationName,
          arrivalStationCode: ticket.arrivalStationCode,
          trainClassifyNo: ticket.stationTrainClassifyCode,
          trainNo: ticket.trainNo,
          roundtripLeg: tickets.length > 1 ? 'return' : 'outbound',
          tripType: tickets.length > 1 ? 'ROUND' : 'ONEWAY',
          trainSeatSet: ticket.seats.map(seat => ({
            carriageNo: seat.carriageNo,
            seatDescription: seat.seatDescription,
            trainCarriageName: `${+seat.carriageNo}호차`,
            trainSeatName: seat.seatDescription,
          })),
        }
      }),
    }
  })
}

export const convertBusMashupToKinesisData = (buses: BusMashup) => {
  return buses.map(bus => {
    const reservations = bus.returns ? [bus.going, bus.returns] : [bus.going]
    return {
      price: bus.price ?? 0,
      busMeta: reservations.map((reservation, reservationIndex) => {
        return {
          returnTrip: !!reservationIndex,
          departureDate: reservation.departureDate,
          departureTime: reservation.departureTime,
          departureTerminalNo: reservation.departureTerminalNo,
          arrivalTerminalNo: reservation.arrivalTerminalNo,
          allocationDepartureTerminalNo: reservation.allocationDepartureTerminalNo,
          allocationArrivalTerminalNo: reservation.allocationArrivalTerminalNo,
          transportationCompanyCode: reservation.transportationCompanyCode,
          busGrade: reservation.busGrade,
          seats: reservation.seats.map(seat => {
            return {
              seatNo: `${seat.seatNo}`,
              ticketType: seat.ticketType,
            }
          }),
          price: reservation.price,
          busDiscountAmount: 0,
        }
      }),
    }
  })
}
